import React, {useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent, useEffectItem} from 'helpers/hooks/utils';
import {useTable} from 'components/organisms/Providers/TableProvider/TableProvider';
import constants from 'helpers/constants';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import Box from 'components/atoms/Layout/Box/Box';
import StyledCollectionEntitiesTagDistributionGraphCard
  from 'components/organisms/Cards/CollectionEntitiesTagDistributionGraphCard/CollectionEntitiesTagDistributionGraphCard.styles';
import {useCollectionStatCollectionTags} from 'services/collection/stat/stat.utils';
import TagGroupPieChart from 'components/organisms/Charts/TagGroupPieChart/TagGroupPieChart';
import {P} from 'components/atoms/Text/Typography/Typography';
import {useLinkNavigate} from 'helpers/hooks/links';
import utils from 'helpers/utils';
import SimpleLoader from 'components/molecules/Loaders/SimpleLoader/SimpleLoader';

const CollectionEntitiesTagDistributionGraphCard = React.forwardRef((props, ref) => {
  const innerProps = useComponentProps(props, 'CollectionEntitiesTagDistributionGraphCard');

  const tableProvider = useTable();
  const collection = tableProvider.context?.data;
  const tagGroups = collection?.tagGroups;
  const graphState = tableProvider.graphState;

  const [breakdown, setBreakdown] = useState(null);

  const navigate = useLinkNavigate();

  const listState = useEffectItem(tableProvider.appliedListState());
  const tagGroupId = graphState.graphSettings['tagDistribution']?.filter?.tagGroupId ?? +tagGroups?.[0]?.groupId;

  const [tagsBreakdown, isLoading] = useCollectionStatCollectionTags(
    collection?.collectionId, listState.search, listState.filter);

  useEffect(() => {
    if (tagsBreakdown) {
      const bd = tagsBreakdown.find((bd) => +bd.groupId === +tagGroupId);
      const tagGroup = (tagGroups || []).find((tg) => +tg.groupId === +tagGroupId);
      setBreakdown({...bd, ...tagGroup});
    }
  }, [tagsBreakdown, tagGroups, tagGroupId]);

  const fields = useMemo(() => {
    const options = (tagGroups || []).map((tg) => ({
      label: tg.name,
      value: +tg.groupId
    }));

    const initialOption = options.find((opt) => +opt.value === +tagGroupId) ?? options?.[0];

    return [{
      name: 'tagGroupId',
      type: constants.formFieldTypes.autocomplete,
      validation: constants.formFieldValidationTypes.text,
      placeholder: 'Select a category',
      initial: initialOption,
      FormFieldProps: {
        autoFocus: false,
        hiddenLabel: true,
        clearable: false,
        size: 'small'
      },
      options: options
    }];
  }, [tagGroups, tagGroupId]);

  const setGraphSettingsEvent = useEffectEvent(graphState.setGraphSettings);
  const handleFilterChange = useCallback((field, value) => {
    setGraphSettingsEvent?.((current) => ({
      ...current,
      'tagDistribution': {
        ...current?.['tagDistribution'],
        filter: {
          ...current?.['tagDistribution']?.filter,
          tagGroupId: value?.value ?? value
        }
      }
    }));
  }, [setGraphSettingsEvent]);

  const handleVisibilityChange = (visibility) => {
    graphState.setGraphSettings((current) => ({
      ...current,
      'tagDistribution': {
        ...current?.['tagDistribution'],
        visibility
      }
    }));
  };

  const handleClick = (e, release, data) => {
    if (data?.id && data?.name && breakdown?.groupId) {
      const prefix = breakdown.groupId;
      const postfix = data?.id < 0 ? 0 : data?.name;
      navigate({
        event: e,
        to: `/collections/${collection.collectionId}/entities?custom=tag:${prefix}_${postfix}`,
        keepSearchParams: true
      });
    }
  };

  return <StyledCollectionEntitiesTagDistributionGraphCard ref={ref} {...innerProps}
                                                           title="Category distribution"
                                                           context={(fields ? <InlineForm onChange={handleFilterChange}
                                                                                          fields={fields} /> : null)}>
    {(utils.isDefined(breakdown) && utils.isDefined(breakdown?.breakdown)) ?
      <Box className="CollectionEntitiesTagDistributionGraphCard-content">
        <TagGroupPieChart breakdown={breakdown}
                          showName={true}
                          showLegend={true}
                          showActiveShape={true}
                          isLoading={isLoading}
                          visibility={graphState.graphSettings['tagDistribution']?.visibility}
                          onClick={handleClick}
                          onVisibilityChange={handleVisibilityChange}/>
      </Box> : null}

    {(!utils.isDefined(breakdown) || !utils.isDefined(breakdown?.breakdown)) ? (
      (!utils.isDefined(breakdown) || isLoading) ?
        <SimpleLoader className="GraphCard-loader" /> :
        <Box className="GraphCard-empty">
          <P>No tag data found</P>
        </Box>
    ) : null}
  </StyledCollectionEntitiesTagDistributionGraphCard>
});

CollectionEntitiesTagDistributionGraphCard.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ])
};

export default CollectionEntitiesTagDistributionGraphCard;
